/* Modals, drawers, confirmation and reason dialogs, toasts. */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { Button, IconButton } from './ui';

export function Modal({ open, title, onClose, children, footer, wide }: {
  open: boolean; title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true">
        <div className="modal-head"><h2>{title}</h2><IconButton icon="x" title="Close" onClick={onClose} /></div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Drawer({ open, title, onClose, children, footer }: {
  open: boolean; title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true">
        <div className="modal-head"><h2>{title}</h2><IconButton icon="x" title="Close" onClick={onClose} /></div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </aside>
    </>
  );
}

export function ConfirmDialog({ open, title, message, confirmLabel = 'Confirm', danger, onConfirm, onClose }: {
  open: boolean; title: string; message: ReactNode; confirmLabel?: string; danger?: boolean;
  onConfirm: () => Promise<unknown> | void; onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Modal open={open} title={title} onClose={onClose}
      footer={<>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant={danger ? 'danger' : 'primary'} loading={busy}
          onClick={async () => { setBusy(true); try { await onConfirm(); onClose(); } finally { setBusy(false); } }}>
          {confirmLabel}
        </Button>
      </>}>
      <div>{message}</div>
    </Modal>
  );
}

/** Every reversal, rejection, unlock or block asks for a recorded reason (min 3 characters). */
export function ReasonDialog({ open, title, label = 'Reason', confirmLabel = 'Submit', danger, extra, onSubmit, onClose }: {
  open: boolean; title: string; label?: string; confirmLabel?: string; danger?: boolean; extra?: ReactNode;
  onSubmit: (reason: string) => Promise<unknown>; onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(''); }, [open]);
  const ok = reason.trim().length >= 3;
  return (
    <Modal open={open} title={title} onClose={onClose}
      footer={<>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant={danger ? 'danger' : 'primary'} disabled={!ok} loading={busy}
          onClick={async () => { setBusy(true); try { await onSubmit(reason.trim()); onClose(); } catch { /* toast shown */ } finally { setBusy(false); } }}>
          {confirmLabel}
        </Button>
      </>}>
      <div className="stack">
        {extra}
        <div className="field">
          <label>{label}<span className="req">*</span></label>
          <textarea className="textarea" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="This is recorded in the audit log" autoFocus />
          <span className="hint">At least 3 characters. Recorded with your name and the time.</span>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- toasts

type ToastTone = 'success' | 'error' | 'warning';
interface ToastItem { id: number; text: string; tone: ToastTone }
const ToastCtx = createContext<(text: string, tone?: ToastTone) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((text: string, tone: ToastTone = 'success') => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { id, text, tone }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), tone === 'error' ? 6000 : 3500);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {items.map((t) => <div key={t.id} className={`toast ${t.tone === 'success' ? '' : t.tone}`}>{t.text}</div>)}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  return useContext(ToastCtx);
}
